import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Phone, Users, CheckSquare, Clock, Mail, Calendar as CalIcon,
  Plus, Trash2, List, LayoutGrid, User as UserIcon, Pencil, Check, X,
  ChevronLeft, ChevronRight, GripVertical
} from 'lucide-react';
import api, { organizationsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CustomFieldsInput from '../components/CustomFieldsInput';
import CustomFieldsDisplay from '../components/CustomFieldsDisplay';
import TaskEditModal from '../components/TaskEditModal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const TYPE_META = {
  call:     { label: 'Call',     Icon: Phone,       color: 'bg-green-100 text-green-700 border-green-200'  },
  meeting:  { label: 'Meeting',  Icon: Users,       color: 'bg-blue-100 text-blue-700 border-blue-200'     },
  todo:     { label: 'To-do',    Icon: CheckSquare, color: 'bg-gray-100 text-gray-700 border-gray-200'     },
  deadline: { label: 'Deadline', Icon: Clock,       color: 'bg-red-100 text-red-700 border-red-200'        },
  email:    { label: 'Email',    Icon: Mail,        color: 'bg-purple-100 text-purple-700 border-purple-200'}
};

const PRIORITY_META = {
  low:    { label: 'Low',    color: 'bg-gray-100 text-gray-600 border-gray-200',   dot: 'bg-gray-400',   sortRank: 0 },
  normal: { label: 'Normal', color: 'bg-blue-50 text-blue-700 border-blue-200',     dot: 'bg-blue-400',   sortRank: 1 },
  high:   { label: 'High',   color: 'bg-red-50 text-red-700 border-red-200',         dot: 'bg-red-500',    sortRank: 2 },
};

const startOfDay = (d) => {
  const x = new Date(d); x.setHours(0, 0, 0, 0); return x;
};

function dueBucket(task) {
  if (task.completed) return 'completed';
  if (!task.dueDate) return 'later';
  const today = startOfDay(new Date()).getTime();
  const due = startOfDay(new Date(task.dueDate)).getTime();
  if (due < today) return 'overdue';
  if (due === today) return 'today';
  const week = today + 6 * 24 * 3600 * 1000;
  if (due <= week) return 'this_week';
  const month = today + 30 * 24 * 3600 * 1000;
  if (due <= month) return 'this_month';
  return 'later';
}

// Meta-style urgency palette:
//   overdue → bold dark red (past the deadline, most urgent)
//   today   → red
//   week    → amber / yellow
//   month   → green
//   later   → neutral gray
// `tone` is the section header text class. `row` is the row border/bg accent
// applied to TaskRow / Row / lead-task cards so urgency reads at a glance.
const BUCKETS = [
  { key: 'overdue',    label: 'Overdue',     tone: 'text-red-900 font-bold', row: 'border-red-300 bg-red-100',         text: 'text-red-900 font-semibold' },
  { key: 'today',      label: 'Today',       tone: 'text-red-700',           row: 'border-red-200 bg-red-50',          text: 'text-red-700 font-medium' },
  { key: 'this_week',  label: 'This Week',   tone: 'text-amber-700',         row: 'border-amber-200 bg-amber-50',      text: 'text-amber-700' },
  { key: 'this_month', label: 'This Month',  tone: 'text-emerald-700',       row: 'border-emerald-200 bg-emerald-50',  text: 'text-emerald-700' },
  { key: 'later',      label: 'Later',       tone: 'text-gray-700',          row: 'border-gray-200 bg-white',          text: 'text-gray-500' },
  { key: 'completed',  label: 'Completed',   tone: 'text-gray-400',          row: 'border-gray-200 bg-white',          text: 'text-gray-400' }
];

const BUCKET_MAP = BUCKETS.reduce((m, b) => { m[b.key] = b; return m; }, {});

const formatDue = (d) => {
  if (!d) return 'No due date';
  const dt = new Date(d);
  const today = startOfDay(new Date()).getTime();
  const diff = (startOfDay(dt).getTime() - today) / (24 * 3600 * 1000);
  if (diff === 0) return `Today, ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  if (diff === 1) return `Tomorrow, ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  if (diff === -1) return `Yesterday`;
  if (diff < 0) return `${Math.abs(diff)} days ago`;
  if (diff < 7) return `In ${diff} days, ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  return dt.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const TaskRow = ({ task, onToggle, onDelete, onEdit, isEditing, onSave, onCancel, canAssignAnyone, orgUsers, selected, onSelectChange, compact = false, dragHandleProps = null }) => {
  const meta = TYPE_META[task.type] || TYPE_META.todo;
  const { Icon } = meta;
  const bucket = dueBucket(task);
  const bucketMeta = BUCKET_MAP[bucket] || BUCKET_MAP.later;
  // Only tint rows for time-sensitive buckets; "later" and "completed" stay
  // neutral so the list doesn't feel washed out.
  const rowAccent = ['overdue', 'today', 'this_week', 'this_month'].includes(bucket)
    ? bucketMeta.row
    : 'border-gray-200';
  const dueText = bucketMeta.text;

  if (isEditing) {
    return (
      <InlineEditRow
        task={task}
        canAssignAnyone={canAssignAnyone}
        orgUsers={orgUsers}
        onSave={onSave}
        onCancel={onCancel}
      />
    );
  }

  const priorityMeta = PRIORITY_META[task.priority || 'normal'];

  // Compact mode for narrow containers (Kanban columns). Stacks chips
  // vertically, hides creator/assignee names, truncates the title.
  // The grip handle on the left is the only drag-trigger zone — the rest
  // of the card is a click target that opens the preview modal. Without
  // this split, dnd's mousedown intercept eats the click event.
  if (compact) {
    return (
      <div className={`group flex items-start gap-1 p-2 border rounded-md hover:shadow-sm transition-shadow ${selected ? 'ring-2 ring-primary-300 border-primary-200 bg-white' : `${rowAccent} ${rowAccent.includes('bg-') ? '' : 'bg-white'}`}`}>
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            className="mt-0.5 -ml-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing flex-shrink-0"
            title="Drag to move"
          >
            <GripVertical size={12} />
          </span>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggle(task); }}
          className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0
            ${task.completed
              ? 'bg-green-500 border-green-500 text-white'
              : 'bg-white border-gray-300 hover:border-green-500 text-transparent hover:text-green-500'}`}
        >
          <Check size={10} strokeWidth={3} />
        </button>
        <button
          type="button"
          onClick={() => onEdit(task)}
          className="flex-1 min-w-0 text-left"
          title="Click to preview"
        >
          <div className="flex items-center gap-1 min-w-0">
            <Icon size={11} className={meta.color.split(' ')[1] || 'text-gray-700'} />
            {task.priority && task.priority !== 'normal' && (
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${priorityMeta.dot}`} title={priorityMeta.label} />
            )}
            <span className={`text-xs font-medium truncate ${task.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
              {task.title}
            </span>
          </div>
          <div className={`text-[10px] mt-0.5 truncate ${task.completed ? 'text-gray-400' : dueText}`}>
            {formatDue(task.dueDate)}
            {task.leadId && (
              <span className="text-primary-600">
                {' · '}{task.leadId.firstName} {task.leadId.lastName}
              </span>
            )}
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className={`group flex items-start gap-3 p-3 border rounded-lg hover:shadow-sm transition-shadow ${selected ? 'ring-2 ring-primary-300 border-primary-200 bg-white' : `${rowAccent} ${rowAccent.includes('bg-') ? '' : 'bg-white'}`}`}>
      {/* Round button = "mark done". Distinct from the square select box so
          the two affordances are never confused at a glance. */}
      <button
        type="button"
        onClick={() => onToggle(task)}
        title={task.completed ? 'Mark not done' : 'Mark done'}
        className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0
          ${task.completed
            ? 'bg-green-500 border-green-500 text-white'
            : 'bg-white border-gray-300 hover:border-green-500 hover:bg-green-50 text-transparent hover:text-green-500'}`}
      >
        <Check size={12} strokeWidth={3} />
      </button>
      {/* Square = "select for bulk action". Hidden by default; only fades in
          on hover or when something is already selected. Keeps the row clean
          when the operator is just scanning, and surfaces the affordance the
          moment they need it. */}
      {onSelectChange && (
        <input
          type="checkbox"
          checked={!!selected}
          onChange={(e) => { e.stopPropagation(); onSelectChange(task, e.target.checked); }}
          className={`mt-1 w-4 h-4 rounded border-gray-300 accent-primary-600 transition-opacity
            ${selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
          title="Select for bulk action"
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap ${meta.color}`}>
            <Icon size={11} /> {meta.label}
          </span>
          {task.priority && task.priority !== 'normal' && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border whitespace-nowrap ${priorityMeta.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${priorityMeta.dot}`} />
              {priorityMeta.label}
            </span>
          )}
          <span className={`font-medium text-sm break-words min-w-0 ${task.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
            {task.title}
          </span>
        </div>
        {task.description && (
          <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{task.description}</p>
        )}
        {task.customFields && (
          <CustomFieldsDisplay entity="task" values={task.customFields} wrapperClassName="mt-2 p-2 bg-gray-50 rounded" />
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-gray-500 min-w-0">
          <span className={`whitespace-nowrap ${task.completed ? '' : dueText}`}>
            {formatDue(task.dueDate)}
          </span>
          {task.leadId && (
            <Link
              to={`/leads/${task.leadId._id}`}
              className="text-primary-600 hover:underline truncate max-w-full"
              title={`${task.leadId.firstName} ${task.leadId.lastName}`}
            >
              {task.leadId.firstName} {task.leadId.lastName}
            </Link>
          )}
          {task.createdBy && (
            <span className="inline-flex items-center gap-1 truncate max-w-full text-gray-400">
              by <span className="text-gray-600">{task.createdBy.name || task.createdBy.email}</span>
            </span>
          )}
          {task.assignedTo && task.assignedTo._id !== task.createdBy?._id && (
            <span className="inline-flex items-center gap-1 truncate max-w-full">
              <UserIcon size={10} /> {task.assignedTo.name || task.assignedTo.email}
            </span>
          )}
          {task.completed && task.completedBy && (
            <span className="text-gray-400">
              done by <span className="text-gray-600">{task.completedBy.name || task.completedBy.email}</span>
              {task.completedAt && ` · ${new Date(task.completedAt).toLocaleDateString()}`}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onEdit(task)}
          className="text-gray-400 hover:text-primary-600"
          title="Edit"
        >
          <Pencil size={14} />
        </button>
        <button
          onClick={() => onDelete(task)}
          className="text-gray-400 hover:text-red-600"
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

// Replaces a TaskRow when the pencil is clicked. Single PATCH on Save.
const InlineEditRow = ({ task, canAssignAnyone, orgUsers, onSave, onCancel }) => {
  const [title, setTitle] = useState(task.title || '');
  const [description, setDescription] = useState(task.description || '');
  const [type, setType] = useState(task.type || 'todo');
  const [priority, setPriority] = useState(task.priority || 'normal');
  const [dueDate, setDueDate] = useState(task.dueDate ? new Date(task.dueDate) : null);
  const [assignedTo, setAssignedTo] = useState(task.assignedTo?._id || task.assignedTo || '');
  const [linkedLead, setLinkedLead] = useState(task.leadId || null);
  const [leadSearch, setLeadSearch] = useState('');
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);

  const { data: leadResults = [] } = useQuery(
    ['task-lead-search-edit', leadSearch],
    () => api.get('/leads', { params: { search: leadSearch, limit: 8 } })
      .then((r) => Array.isArray(r.data) ? r.data : (r.data?.leads || [])),
    { enabled: showLeadDropdown && leadSearch.length >= 1, staleTime: 30 * 1000 }
  );

  const save = () => {
    if (!title.trim()) return toast.error('Title is required');
    const patch = {
      title: title.trim(),
      description: description.trim(),
      type,
      priority,
      dueDate: dueDate ? dueDate.toISOString() : null,
      leadId: linkedLead?._id || null,
    };
    if (canAssignAnyone) patch.assignedTo = assignedTo || null;
    onSave(task, patch);
  };

  return (
    <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg space-y-2">
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)…"
        rows={2}
        className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white resize-none"
      />
      <div className="flex flex-wrap gap-2 items-center">
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
        >
          {Object.entries(TYPE_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
          title="Priority"
        >
          {Object.entries(PRIORITY_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <DatePicker
          selected={dueDate}
          onChange={(d) => setDueDate(d)}
          showTimeSelect
          timeIntervals={15}
          dateFormat="MMM d, yyyy h:mm aa"
          placeholderText="No due date"
          className="px-2 py-1.5 text-sm border border-gray-200 rounded-md"
          isClearable
        />
        {canAssignAnyone && (
          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
          >
            <option value="">Unassigned</option>
            {orgUsers.map((u) => (
              <option key={u._id} value={u._id}>{u.name || u.email}</option>
            ))}
          </select>
        )}
      </div>
      <div className="relative">
        {linkedLead ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-primary-200 rounded-md text-sm">
            <span className="font-medium text-primary-900">
              {linkedLead.firstName} {linkedLead.lastName}
            </span>
            {linkedLead.company && (
              <span className="text-xs text-gray-500">— {linkedLead.company}</span>
            )}
            <button
              type="button"
              onClick={() => { setLinkedLead(null); setLeadSearch(''); }}
              className="ml-auto text-gray-400 hover:text-red-600"
              title="Unlink lead"
            >×</button>
          </div>
        ) : (
          <>
            <input
              value={leadSearch}
              onChange={(e) => { setLeadSearch(e.target.value); setShowLeadDropdown(true); }}
              onFocus={() => setShowLeadDropdown(true)}
              onBlur={() => setTimeout(() => setShowLeadDropdown(false), 150)}
              placeholder="Link to a lead (optional)…"
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
            />
            {showLeadDropdown && leadResults.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {leadResults.slice(0, 8).map((l) => (
                  <button
                    key={l._id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setLinkedLead(l); setShowLeadDropdown(false); setLeadSearch(''); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">{l.firstName} {l.lastName}</div>
                    {l.company && <div className="text-xs text-gray-500">{l.company}</div>}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex gap-2">
        <button onClick={save} className="inline-flex items-center gap-1 px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white text-xs rounded-md">
          <Check size={12} /> Save
        </button>
        <button onClick={onCancel} className="inline-flex items-center gap-1 px-3 py-1 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 text-xs rounded-md">
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
};

const NewTaskForm = ({ onSubmit, submitting, defaults = {}, currentUser }) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState(defaults.type || 'todo');
  const [priority, setPriority] = useState('normal');
  // dueDate is a Date object now (react-datepicker handles serialization),
  // not a `YYYY-MM-DDTHH:MM` string. Avoids the timezone-rounding nonsense
  // the native datetime-local input had with our hour/minute selection.
  const [dueDate, setDueDate] = useState(null);
  const [description, setDescription] = useState('');
  const [leadSearch, setLeadSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [assignedTo, setAssignedTo] = useState('');
  const [customFields, setCustomFields] = useState({});
  const [showMore, setShowMore] = useState(false);

  // Admins (view_all_leads) can pick any teammate as assignee. Reps can only
  // create tasks for themselves and we hide the picker entirely.
  // Strict check — only admins / superadmins (with explicit view_all_leads
  // === true) can hand tasks off to teammates. Anyone else falls into the
  // self-only path, regardless of whether the permission is unset or false.
  const canAssignAnyone = currentUser?.permissions?.view_all_leads === true || currentUser?.role === 'superadmin';

  // Lead search — server filters by access (rep only sees own leads).
  const { data: leadResults = [] } = useQuery(
    ['task-lead-search', leadSearch],
    () => api.get('/leads', { params: { search: leadSearch, limit: 8 } })
      .then((r) => Array.isArray(r.data) ? r.data : (r.data?.leads || [])),
    { enabled: showLeadDropdown && leadSearch.length >= 1, staleTime: 30 * 1000 }
  );

  const { data: orgUsersResp } = useQuery(
    ['organization', 'users'],
    () => organizationsAPI.getUsers(),
    { enabled: canAssignAnyone, staleTime: 60_000 }
  );
  const orgUsers = orgUsersResp?.data?.users || [];

  const submit = (e) => {
    e?.preventDefault?.();
    if (!title.trim()) return toast.error('Title is required');
    const payload = {
      title: title.trim(),
      type,
      priority,
      description: description.trim() || undefined,
      dueDate: dueDate ? dueDate.toISOString() : null,
    };
    if (selectedLead) payload.leadId = selectedLead._id;
    if (canAssignAnyone && assignedTo) payload.assignedTo = assignedTo;
    if (Object.keys(customFields).length > 0) payload.customFields = customFields;
    onSubmit(payload);
    setTitle(''); setDescription(''); setDueDate(null); setType('todo'); setPriority('normal');
    setCustomFields({}); setSelectedLead(null); setLeadSearch(''); setAssignedTo('');
  };

  const setQuickDate = (offsetDays, hour = 9, minute = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    d.setHours(hour, minute, 0, 0);
    setDueDate(d);
  };

  return (
    <form onSubmit={submit} className="p-3 bg-white border border-gray-200 rounded-lg space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="New task title..."
          className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="px-2 py-1.5 text-sm border border-gray-200 rounded-md"
        >
          {Object.entries(TYPE_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="px-2 py-1.5 text-sm border border-gray-200 rounded-md"
          title="Priority"
        >
          {Object.entries(PRIORITY_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <DatePicker
          selected={dueDate}
          onChange={(d) => setDueDate(d)}
          showTimeSelect
          timeIntervals={15}
          timeCaption="Time"
          dateFormat="MMM d, yyyy h:mm aa"
          placeholderText="Due date & time"
          minDate={new Date()}
          className="px-2 py-1.5 text-sm border border-gray-200 rounded-md w-[200px]"
          popperPlacement="bottom-start"
          isClearable
        />
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
        >
          <Plus size={14} /> Add
        </button>
      </div>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)…"
        rows={2}
        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
      />
      <div className="flex flex-wrap gap-1">
        {[
          { label: 'In 1 hour', fn: () => { const d = new Date(); d.setHours(d.getHours() + 1, 0, 0, 0); setDueDate(d); } },
          { label: 'Today 5 PM', fn: () => setQuickDate(0, 17) },
          { label: 'Tomorrow 9 AM', fn: () => setQuickDate(1, 9) },
          { label: 'In 3 days', fn: () => setQuickDate(3, 9) },
          { label: 'Next week', fn: () => setQuickDate(7, 9) },
        ].map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={p.fn}
            className="px-2 py-0.5 text-[11px] bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full"
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Lead + assignee row. Lead picker autocompletes against /leads which
          is already access-filtered by role on the server, so reps only see
          leads assigned to them. Assignee picker is hidden for reps. */}
      <div className="flex flex-wrap items-start gap-2">
        <div className="relative flex-1 min-w-[220px]">
          {selectedLead ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-md text-sm">
              <span className="font-medium text-primary-900">
                {selectedLead.firstName} {selectedLead.lastName}
              </span>
              {selectedLead.company && (
                <span className="text-xs text-primary-600">— {selectedLead.company}</span>
              )}
              <button
                type="button"
                onClick={() => { setSelectedLead(null); setLeadSearch(''); }}
                className="ml-auto text-primary-600 hover:text-red-600"
                title="Clear lead"
              >×</button>
            </div>
          ) : (
            <>
              <input
                value={leadSearch}
                onChange={(e) => { setLeadSearch(e.target.value); setShowLeadDropdown(true); }}
                onFocus={() => setShowLeadDropdown(true)}
                onBlur={() => setTimeout(() => setShowLeadDropdown(false), 150)}
                placeholder="Link to a lead (optional)…"
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md"
              />
              {showLeadDropdown && leadResults.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {leadResults.slice(0, 8).map((l) => (
                    <button
                      key={l._id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setSelectedLead(l); setShowLeadDropdown(false); setLeadSearch(''); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">{l.firstName} {l.lastName}</div>
                      {l.company && <div className="text-xs text-gray-500">{l.company}</div>}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        {canAssignAnyone ? (
          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
          >
            <option value="">Assign to me (default)</option>
            {orgUsers.map((u) => (
              <option key={u._id} value={u._id}>{u.name || u.email}</option>
            ))}
          </select>
        ) : (
          <span className="px-2 py-1.5 text-xs text-gray-500 italic">
            Tasks you create are visible only to you.
          </span>
        )}
      </div>
      <CollapsibleCustomFields entity="task" values={customFields} onChange={setCustomFields} open={showMore} onToggle={() => setShowMore(!showMore)} />
    </form>
  );
};

const CollapsibleCustomFields = ({ entity, values, onChange, open, onToggle }) => {
  const { data: defs = [] } = useQuery(
    ['custom-fields', entity],
    () => api.get('/custom-fields', { params: { entity } }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );
  if (defs.length === 0) return null;
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="text-xs text-primary-600 hover:text-primary-700"
      >
        {open ? '− Hide' : '+ Add'} custom fields ({defs.length})
      </button>
      {open && (
        <div className="mt-2 p-3 bg-gray-50 rounded-md">
          <CustomFieldsInput entity={entity} values={values} onChange={onChange} />
        </div>
      )}
    </div>
  );
};

const Tasks = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  // Strict check — only admins / superadmins (with explicit view_all_leads
  // === true) can hand tasks off to teammates. Anyone else falls into the
  // self-only path, regardless of whether the permission is unset or false.
  const canAssignAnyone = currentUser?.permissions?.view_all_leads === true || currentUser?.role === 'superadmin';
  const [view, setView] = useState('list'); // 'list' | 'kanban'
  const [scope, setScope] = useState('me'); // 'me' | 'all'
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('due'); // 'due' | 'created' | 'title' | 'type' | 'priority'
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Same org-users source the NewTaskForm uses, so the inline edit row can
  // reassign tasks to teammates without re-fetching.
  const { data: orgUsersResp } = useQuery(
    ['organization', 'users'],
    () => organizationsAPI.getUsers(),
    { enabled: canAssignAnyone, staleTime: 60_000 }
  );
  const orgUsers = orgUsersResp?.data?.users || [];

  const { data: tasks = [], isLoading } = useQuery(
    ['tasks', { scope, showCompleted }],
    () => api.get('/tasks', {
      params: {
        ...(scope === 'me' ? { assignedTo: 'me' } : {}),
        ...(showCompleted ? {} : { completed: 'false' })
      }
    }).then((r) => r.data)
  );

  const createMutation = useMutation(
    (body) => api.post('/tasks', body).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Task added');
        queryClient.invalidateQueries(['tasks']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Create failed')
    }
  );

  const patchMutation = useMutation(
    ({ id, patch }) => api.patch(`/tasks/${id}`, patch).then((r) => r.data),
    { onSuccess: () => queryClient.invalidateQueries(['tasks']), onError: (err) => toast.error(err.response?.data?.error || 'Update failed') }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/tasks/${id}`).then((r) => r.data),
    {
      onSuccess: () => { toast.success('Task deleted'); queryClient.invalidateQueries(['tasks']); },
      onError: (err) => toast.error(err.response?.data?.error || 'Delete failed')
    }
  );

  const filtered = useMemo(() => {
    let out = tasks;
    if (typeFilter !== 'all') out = out.filter((t) => t.type === typeFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((t) => {
        if ((t.title || '').toLowerCase().includes(q)) return true;
        if ((t.description || '').toLowerCase().includes(q)) return true;
        const leadName = `${t.leadId?.firstName || ''} ${t.leadId?.lastName || ''}`.toLowerCase();
        if (leadName.includes(q)) return true;
        return false;
      });
    }
    // Sort within filtered set. Date buckets are still derived from dueDate
    // so the visible order inside each bucket follows this comparator.
    const cmp = {
      due: (a, b) => {
        const ax = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bx = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return ax - bx;
      },
      created: (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
      title: (a, b) => (a.title || '').localeCompare(b.title || ''),
      type: (a, b) => (a.type || '').localeCompare(b.type || ''),
      priority: (a, b) => {
        const ar = (PRIORITY_META[a.priority || 'normal'] || PRIORITY_META.normal).sortRank;
        const br = (PRIORITY_META[b.priority || 'normal'] || PRIORITY_META.normal).sortRank;
        return br - ar;
      },
    }[sortBy] || ((a, b) => 0);
    return [...out].sort(cmp);
  }, [tasks, typeFilter, search, sortBy]);

  const byBucket = useMemo(() => {
    const out = { overdue: [], today: [], this_week: [], this_month: [], later: [], completed: [] };
    for (const t of filtered) {
      out[dueBucket(t)].push(t);
    }
    return out;
  }, [filtered]);

  const [editingTask, setEditingTask] = useState(null);
  // Per-trigger initial mode. List view → 'edit' (Pencil jumps into
  // the form); Kanban / Calendar → 'view' (quick read-only preview
  // first, matches how operators scan cards). Stored alongside the
  // task because the modal is mounted once.
  const [editingInitialMode, setEditingInitialMode] = useState('view');
  const editingId = editingTask?._id || null; // back-compat for the no-op inline ref
  const handleToggle = (task) => patchMutation.mutate({ id: task._id, patch: { completed: !task.completed } });
  const handleDelete = (task) => {
    if (window.confirm('Delete this task?')) deleteMutation.mutate(task._id);
  };
  const handleEdit = (task) => { setEditingInitialMode('view'); setEditingTask(task); };
  const handleEditFromList = (task) => { setEditingInitialMode('edit'); setEditingTask(task); };
  const handleSaveEdit = (task, patch) => {
    patchMutation.mutate({ id: task._id, patch });
    setEditingTask(null);
  };
  const handleCancelEdit = () => setEditingTask(null);
  const handleModalDelete = (task) => {
    deleteMutation.mutate(task._id);
    setEditingTask(null);
  };

  const toggleSelected = (task, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(task._id);
      else next.delete(task._id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = () => setSelectedIds(new Set(filtered.map((t) => t._id)));

  const bulkMutation = useMutation(
    (body) => api.post('/tasks/bulk', body).then((r) => r.data),
    {
      onSuccess: (r, vars) => {
        const verb = {
          complete: 'completed',
          uncomplete: 'reopened',
          delete: 'deleted',
          reassign: 'reassigned',
          priority: 'updated',
        }[vars.action] || 'updated';
        toast.success(`${r.modified ?? r.deleted ?? 0} task(s) ${verb}.`);
        clearSelection();
        queryClient.invalidateQueries(['tasks']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Bulk action failed'),
    }
  );

  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const task = tasks.find((t) => t._id === draggableId);
    if (!task) return;

    // Date buckets are derived from dueDate ranges, so to "move" a task
    // into a different bucket we have to set its dueDate to a representative
    // moment inside that range. Time-of-day is preserved when possible so a
    // "9am call" stays a 9am call after rescheduling.
    const keepTime = (d) => {
      if (task.dueDate) {
        const orig = new Date(task.dueDate);
        d.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
      } else {
        d.setHours(9, 0, 0, 0);
      }
      return d;
    };

    if (destination.droppableId === 'completed') {
      patchMutation.mutate({ id: draggableId, patch: { completed: true } });
      return;
    }
    const patch = {};
    if (source.droppableId === 'completed') {
      patch.completed = false;
    }
    if (destination.droppableId === 'today') {
      const d = new Date(); patch.dueDate = keepTime(d).toISOString();
    } else if (destination.droppableId === 'this_week') {
      const d = new Date(); d.setDate(d.getDate() + 3); patch.dueDate = keepTime(d).toISOString();
    } else if (destination.droppableId === 'later') {
      const d = new Date(); d.setDate(d.getDate() + 14); patch.dueDate = keepTime(d).toISOString();
    } else if (destination.droppableId === 'overdue') {
      // Refuse — operators shouldn't intentionally backdate. Quiet skip.
      return;
    }
    if (Object.keys(patch).length === 0) return;
    patchMutation.mutate({ id: draggableId, patch });
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tasks</h1>
          <p className="text-sm text-gray-600 mt-1">What needs to get done</p>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setView('list')}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
          >
            <List size={14} /> List
          </button>
          <button
            onClick={() => setView('kanban')}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'kanban' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
          >
            <LayoutGrid size={14} /> Kanban
          </button>
          <button
            onClick={() => setView('calendar')}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'calendar' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
          >
            <CalIcon size={14} /> Calendar
          </button>
        </div>
      </div>

      {/* Search + sort row */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tasks by title, description, or lead…"
          className="flex-1 min-w-[260px] px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <label className="text-xs text-gray-500">Sort by</label>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
        >
          <option value="due">Due date</option>
          <option value="priority">Priority</option>
          <option value="created">Recently created</option>
          <option value="title">Title (A-Z)</option>
          <option value="type">Type</option>
        </select>
      </div>

      {/* Bulk action bar — appears only when something is selected. */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
          <span className="text-sm font-medium text-primary-900">
            {selectedIds.size} selected
          </span>
          <button
            onClick={selectAllVisible}
            className="px-2 py-1 text-xs text-primary-700 hover:text-primary-900"
          >
            Select all visible ({filtered.length})
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => bulkMutation.mutate({ ids: [...selectedIds], action: 'complete' })}
            disabled={bulkMutation.isLoading}
            className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-md font-medium"
          >
            Mark done
          </button>
          <button
            onClick={() => bulkMutation.mutate({ ids: [...selectedIds], action: 'uncomplete' })}
            disabled={bulkMutation.isLoading}
            className="px-3 py-1 text-xs bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 rounded-md font-medium"
          >
            Reopen
          </button>
          <select
            onChange={(e) => {
              if (!e.target.value) return;
              bulkMutation.mutate({ ids: [...selectedIds], action: 'priority', priority: e.target.value });
              e.target.value = '';
            }}
            defaultValue=""
            className="px-2 py-1 text-xs border border-gray-200 rounded-md bg-white"
          >
            <option value="" disabled>Set priority…</option>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
          {canAssignAnyone && (
            <select
              onChange={(e) => {
                if (!e.target.value) return;
                bulkMutation.mutate({ ids: [...selectedIds], action: 'reassign', assignedTo: e.target.value });
                e.target.value = '';
              }}
              defaultValue=""
              className="px-2 py-1 text-xs border border-gray-200 rounded-md bg-white"
            >
              <option value="" disabled>Reassign to…</option>
              {orgUsers.map((u) => (
                <option key={u._id} value={u._id}>{u.name || u.email}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => {
              if (window.confirm(`Delete ${selectedIds.size} task(s)? This can't be undone.`)) {
                bulkMutation.mutate({ ids: [...selectedIds], action: 'delete' });
              }
            }}
            disabled={bulkMutation.isLoading}
            className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-md font-medium"
          >
            Delete
          </button>
          <button
            onClick={clearSelection}
            className="ml-auto px-2 py-1 text-xs text-gray-500 hover:text-gray-900"
          >
            Clear
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1 text-sm">
          <button
            onClick={() => setScope('me')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${scope === 'me' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            My Tasks
          </button>
          <button
            onClick={() => setScope('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${scope === 'all' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            All
          </button>
        </div>
        <span className="text-gray-300">|</span>
        <button
          onClick={() => setTypeFilter('all')}
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${typeFilter === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          All types
        </button>
        {Object.entries(TYPE_META).map(([k, v]) => {
          const { Icon } = v;
          return (
            <button
              key={k}
              onClick={() => setTypeFilter(typeFilter === k ? 'all' : k)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${typeFilter === k ? v.color + ' ring-2 ring-offset-1 ring-primary-300' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              <Icon size={11} /> {v.label}
            </button>
          );
        })}
        <span className="text-gray-300 ml-auto">|</span>
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
          />
          Show completed
        </label>
      </div>

      {/* Quick add */}
      <div className="mb-4">
        <NewTaskForm onSubmit={createMutation.mutate} submitting={createMutation.isLoading} currentUser={currentUser} />
      </div>

      {isLoading ? (
        <p className="text-gray-400 text-sm">Loading...</p>
      ) : view === 'calendar' ? (
        <CalendarView
          tasks={filtered}
          onEdit={handleEdit}
          onToggle={handleToggle}
          onReschedule={(task, newDate) => patchMutation.mutate({
            id: task._id,
            patch: { dueDate: newDate ? newDate.toISOString() : null }
          })}
        />
      ) : view === 'list' ? (
        <div className="space-y-6">
          {BUCKETS.map((b) => {
            const items = byBucket[b.key];
            if (!items.length) return null;
            if (b.key === 'completed' && !showCompleted) return null;
            return (
              <div key={b.key}>
                <h3 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${b.tone}`}>
                  {b.label} <span className="text-gray-400 font-normal">({items.length})</span>
                </h3>
                <div className="space-y-2">
                  {items.map((t) => (
                    <TaskRow
                      key={t._id}
                      task={t}
                      onToggle={handleToggle}
                      onDelete={handleDelete}
                      onEdit={handleEditFromList}
                      // List view edits via the TaskEditModal (rendered
                      // at the bottom of this component) opened
                      // straight into edit mode — not the InlineEditRow
                      // and not the view-mode preview. Inline is for
                      // narrow Kanban cards; preview is for the
                      // Kanban / Calendar quick-read flow.
                      isEditing={false}
                      onSave={handleSaveEdit}
                      onCancel={handleCancelEdit}
                      canAssignAnyone={canAssignAnyone}
                      orgUsers={orgUsers}
                      selected={selectedIds.has(t._id)}
                      onSelectChange={toggleSelected}
                    />
                  ))}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <p className="text-gray-400 text-sm py-8 text-center">No tasks. Add one above.</p>}
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {BUCKETS.filter((b) => showCompleted || b.key !== 'completed').map((b) => {
              const items = byBucket[b.key];
              return (
                <div key={b.key} className="bg-gray-50 rounded-lg border border-gray-200 p-2 flex flex-col">
                  <h3 className={`text-xs font-semibold uppercase tracking-wider mb-2 px-1 ${b.tone}`}>
                    {b.label} <span className="text-gray-400 font-normal">({items.length})</span>
                  </h3>
                  <Droppable droppableId={b.key}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-2 flex-1 min-h-[80px] rounded transition-colors ${snapshot.isDraggingOver ? 'bg-primary-50' : ''}`}
                      >
                        {items.map((t, idx) => (
                          <Draggable key={t._id} draggableId={t._id} index={idx}>
                            {(prov, snap) => (
                              <div
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                className={`${snap.isDragging ? 'shadow-lg rotate-1' : ''}`}
                              >
                                <TaskRow
                                  task={t}
                                  onToggle={handleToggle}
                                  onDelete={handleDelete}
                                  onEdit={handleEdit}
                                  // Kanban also routes edits through
                                  // the modal; without this the card
                                  // behind the open preview morphed
                                  // into an inline edit form ("the
                                  // page outside the modal breaks").
                                  isEditing={false}
                                  onSave={handleSaveEdit}
                                  onCancel={handleCancelEdit}
                                  canAssignAnyone={canAssignAnyone}
                                  orgUsers={orgUsers}
                                  selected={selectedIds.has(t._id)}
                                  onSelectChange={toggleSelected}
                                  compact
                                  dragHandleProps={prov.dragHandleProps}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {items.length === 0 && !snapshot.isDraggingOver && (
                          <p className="text-center text-xs text-gray-400 py-4">Empty</p>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}

      <TaskEditModal
        task={editingTask}
        isOpen={!!editingTask}
        onClose={handleCancelEdit}
        onSave={handleSaveEdit}
        onDelete={handleModalDelete}
        onToggle={handleToggle}
        initialMode={editingInitialMode}
      />
    </div>
  );
};

// Month calendar — tasks bucketed by their dueDate's calendar day. Tasks
// without a dueDate aren't placed on the grid (they'd need a separate
// "Unscheduled" bucket; out of scope for v1). Click a task to jump to edit.
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarView = ({ tasks, onEdit, onToggle, onReschedule }) => {
  // Drag a chip from one day to another → patch dueDate to the dropped day
  // (keeping the original time-of-day so rescheduling doesn't accidentally
  // wipe the rep's "9am call" intent). Unscheduled tasks get the dropped
  // day at 9am as a sensible default.
  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const task = tasks.find((t) => t._id === draggableId);
    if (!task) return;

    // droppableId formats:
    //   day-YYYY-M-D  (calendar cell)
    //   unscheduled   (the amber strip)
    if (destination.droppableId === 'unscheduled') {
      onReschedule(task, null);
      return;
    }
    const m = /^day-(\d+)-(\d+)-(\d+)$/.exec(destination.droppableId);
    if (!m) return;
    const newDate = new Date(Number(m[1]), Number(m[2]), Number(m[3]));
    if (task.dueDate) {
      const orig = new Date(task.dueDate);
      newDate.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
    } else {
      newDate.setHours(9, 0, 0, 0);
    }
    onReschedule(task, newDate);
  };

  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    d.setDate(1); d.setHours(0, 0, 0, 0);
    return d;
  });

  const monthStart = cursor;
  const monthEnd = useMemo(() => {
    const d = new Date(cursor);
    d.setMonth(d.getMonth() + 1);
    d.setDate(0); // last day of cursor's month
    return d;
  }, [cursor]);

  // 6×7 grid leading from the Sunday before monthStart to the Saturday
  // after monthEnd. Always 42 cells so the grid height doesn't jump.
  const days = useMemo(() => {
    const startWeekday = monthStart.getDay();
    const gridStart = new Date(monthStart);
    gridStart.setDate(1 - startWeekday);
    const out = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      out.push(d);
    }
    return out;
  }, [monthStart]);

  const tasksByDay = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      if (!t.dueDate) continue;
      const d = new Date(t.dueDate);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return map;
  }, [tasks]);

  const today = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);
  const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;

  const monthLabel = cursor.toLocaleDateString([], { month: 'long', year: 'numeric' });
  const unscheduled = tasks.filter((t) => !t.dueDate);

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <button
          onClick={() => { const d = new Date(cursor); d.setMonth(d.getMonth() - 1); setCursor(d); }}
          className="p-1.5 hover:bg-gray-100 rounded-md text-gray-600"
          title="Previous month"
        >
          <ChevronLeft size={16} />
        </button>
        <h2 className="text-lg font-semibold text-gray-900 min-w-[180px]">{monthLabel}</h2>
        <button
          onClick={() => { const d = new Date(cursor); d.setMonth(d.getMonth() + 1); setCursor(d); }}
          className="p-1.5 hover:bg-gray-100 rounded-md text-gray-600"
          title="Next month"
        >
          <ChevronRight size={16} />
        </button>
        <button
          onClick={() => {
            const d = new Date();
            d.setDate(1); d.setHours(0, 0, 0, 0);
            setCursor(d);
          }}
          className="px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
        >
          Today
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w} className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 px-2 py-1">
            {w}
          </div>
        ))}
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d) => {
            const inMonth = d.getMonth() === monthStart.getMonth();
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            const droppableId = `day-${key}`;
            const dayTasks = tasksByDay.get(key) || [];
            const isToday = key === todayKey;
            return (
              <Droppable key={key} droppableId={droppableId}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`min-h-[110px] p-1.5 rounded-md border text-xs flex flex-col gap-1 transition-colors ${
                      snapshot.isDraggingOver ? 'border-primary-400 bg-primary-100/40 ring-2 ring-primary-300'
                      : isToday ? 'border-primary-400 bg-primary-50/30'
                      : inMonth ? 'border-gray-200 bg-white'
                      : 'border-gray-100 bg-gray-50/40'
                    }`}
                  >
                    <div className={`text-[11px] font-semibold ${
                      isToday ? 'text-primary-700'
                      : inMonth ? 'text-gray-700'
                      : 'text-gray-400'
                    }`}>
                      {d.getDate()}
                    </div>
                    {dayTasks.slice(0, 3).map((t, idx) => {
                      const meta = TYPE_META[t.type] || TYPE_META.todo;
                      const priorityMeta = PRIORITY_META[t.priority || 'normal'];
                      const isOverdue = !t.completed && new Date(t.dueDate) < today;
                      return (
                        <Draggable key={t._id} draggableId={t._id} index={idx}>
                          {(prov, snap) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              className={`flex items-center gap-0.5 rounded text-[10px] transition-colors hover:opacity-80 ${
                                snap.isDragging ? 'shadow-lg rotate-1 z-50' : ''
                              } ${
                                t.completed ? 'bg-gray-100 text-gray-400 line-through' : isOverdue ? 'bg-red-50 text-red-700' : meta.color
                              }`}
                              title={`${t.title}${t.leadId ? ` — ${t.leadId.firstName} ${t.leadId.lastName}` : ''}`}
                            >
                              <span
                                {...prov.dragHandleProps}
                                className="px-0.5 cursor-grab active:cursor-grabbing opacity-50 hover:opacity-100"
                                title="Drag to reschedule"
                              >
                                <GripVertical size={10} />
                              </span>
                              <button
                                onClick={() => onEdit(t)}
                                className="flex items-center gap-1 flex-1 min-w-0 px-0.5 py-0.5 text-left truncate"
                              >
                                {t.priority && t.priority !== 'normal' && (
                                  <span className={`w-1 h-1 rounded-full flex-shrink-0 ${priorityMeta.dot}`} />
                                )}
                                <span className="truncate flex-1 min-w-0">{t.title}</span>
                              </button>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                    {dayTasks.length > 3 && (
                      <div className="text-[9px] text-gray-500 px-1">+{dayTasks.length - 3} more</div>
                    )}
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>

        {/* Unscheduled drop zone — drag any task here to clear its dueDate.
            Always rendered (even when empty) so the operator has a visible
            "park it for later" target. */}
        <Droppable droppableId="unscheduled" direction="horizontal">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`mt-4 p-3 rounded-lg border-2 border-dashed transition-colors ${
                snapshot.isDraggingOver ? 'border-amber-400 bg-amber-100' : 'border-amber-200 bg-amber-50'
              }`}
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-900 mb-2">
                Unscheduled — no due date ({unscheduled.length}) · drop here to clear due date
              </div>
              <div className="flex flex-wrap gap-1 min-h-[28px]">
                {unscheduled.slice(0, 12).map((t, idx) => {
                  const meta = TYPE_META[t.type] || TYPE_META.todo;
                  return (
                    <Draggable key={t._id} draggableId={t._id} index={idx}>
                      {(prov, snap) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          className={`flex items-center gap-0.5 rounded ${meta.color} ${snap.isDragging ? 'shadow-lg' : ''}`}
                          title={t.title}
                        >
                          <span
                            {...prov.dragHandleProps}
                            className="pl-1 cursor-grab active:cursor-grabbing opacity-50 hover:opacity-100"
                          >
                            <GripVertical size={11} />
                          </span>
                          <button
                            onClick={() => onEdit(t)}
                            className="text-[11px] px-2 py-1 hover:opacity-80"
                          >
                            {t.title}
                          </button>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
                {unscheduled.length > 12 && (
                  <span className="text-xs text-amber-700 self-center">+{unscheduled.length - 12} more</span>
                )}
              </div>
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default Tasks;
